
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Menu, LogOut, ArrowLeft, 
  LayoutDashboard, FileText, Users, AlertCircle, 
  Folder, Map, CreditCard, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

import { AuthUserLite } from "@/types/domain";

interface AdminMobileMenuProps {
  user: AuthUserLite | null;
  signOut: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const AdminMobileMenu = ({ user, signOut, isOpen, setIsOpen }: AdminMobileMenuProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();
  
  const adminMenuItems = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/admin"
    },
    {
      label: "Review Listings",
      icon: FileText,
      path: "/admin/review-listings"
    },
    {
      label: "User Management",
      icon: Users,
      path: "/admin/users"
    },
    {
      label: "Reported Reviews",
      icon: AlertCircle,
      path: "/admin/reported-reviews"
    },
    {
      label: "Manage Categories",
      icon: Folder,
      path: "/admin/categories"
    },
    {
      label: "Manage Areas",
      icon: Map,
      path: "/admin/areas"
    },
    {
      label: "Subscription Management",
      icon: CreditCard,
      path: "/admin/subscriptions"
    },
    {
      label: "Application Settings",
      icon: Settings,
      path: "/admin/settings"
    }
  ];
  
  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path !== '/admin' && location.pathname.startsWith(path)) return true;
    if (path === '/admin' && location.pathname === '/admin') return true;
    return false;
  };
  
  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open);
    setIsOpen(open);
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label="Admin Menu"
        >
          {isOpen ? <X /> : <Menu />}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <SheetHeader className="mb-6">
          <SheetTitle>Admin Panel</SheetTitle>
          {user && (
            <div className="flex flex-col items-center mt-2">
              <p className="text-sm font-medium">{(user?.user_metadata as Record<string, unknown>)?.full_name as string || user?.user_metadata?.first_name || "Admin"}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          )}
        </SheetHeader>
        <nav className="flex flex-col gap-2">
          {adminMenuItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSheetOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                isActive(item.path) 
                  ? 'bg-whatsapp/10 text-whatsapp font-medium' 
                  : 'hover:bg-gray-100'
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
          
          <Separator className="my-4" />
          
          <Link
            to="/"
            onClick={() => setSheetOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Return to Site
          </Link>
          
          <Separator className="my-2" />
          
          <button
            onClick={() => {
              signOut();
              setSheetOpen(false);
            }}
            className="flex items-center gap-3 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-md transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default AdminMobileMenu;
